import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme as useMuiTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {
  getAdminPanelMachineConfiguration,
  getAvailableMachinesByLine,
  isAdminPanelConfigApiEnabled,
  saveAdminPanelConfiguration,
  type AdminPanelServerRow,
  type MachineOption,
} from '../services/adminPanelService';

interface AdminPanelDialogProps {
  open: boolean;
  onClose: () => void;
}

interface ConfigurationRow {
  id: string;
  serverRowId?: string;
  header: string;
  parameter: string;
  goldenSettingRange: string;
  values: Record<string, string>;
  skuServerRowIds?: Record<string, string>;
}

interface NotificationState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

const AdminPanelDialog: React.FC<AdminPanelDialogProps> = ({ open, onClose }) => {
  const theme = useMuiTheme();

  // Fetch machines dynamically from backend - completely dynamic, no hardcoded values
  const [machineOptions, setMachineOptions] = useState<MachineOption[]>([]);

  // SKU columns should come strictly from DB data for each machine.
  const defaultSkuColumns: string[] = [];

  // All configuration data now comes from the database
  const buildRowsForMachine = (): ConfigurationRow[] => {
    // Return empty array - all data must come from database
    // No fallback to hardcoded defaults to ensure data consistency
    return [];
  };

  const formatParameterForDisplay = (value: string): string => {
    const normalized = (value || '').trim();
    if (!normalized) {
      return '';
    }

    const words = normalized
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .split(/\s+/)
      .filter(Boolean);

    if (words.length === 0) {
      return '';
    }

    return words
      .map((word) => {
        const lower = word.toLowerCase();
        return `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
      })
      .join(' ');
  };

  const normalizeLineCode = (lineCode: string): 'line1' | 'line2' | 'unknown' => {
    const normalized = (lineCode || '').toLowerCase().replace(/[-\s]+/g, '_').trim();

    if (normalized === 'line_1' || normalized === 'line1' || normalized === 'line_01') {
      return 'line1';
    }

    if (normalized === 'line_2' || normalized === 'line2' || normalized === 'line_02') {
      return 'line2';
    }

    return 'unknown';
  };

  const extractSkuColumnsFromServerData = (serverRows: AdminPanelServerRow[]): string[] => {
    const uniqueSkus = new Set<string>();

    const addSku = (skuValue: string | undefined | null) => {
      const normalized = (skuValue || '').trim();
      if (!normalized) {
        return;
      }

      // Use the SKU value exactly as it comes from the response (including "all")
      uniqueSkus.add(normalized);
    };

    // Extract SKU columns ONLY from the actual sku field in database rows
    serverRows.forEach((row) => {
      addSku(row.sku);
    });

    return Array.from(uniqueSkus).sort((a, b) => a.localeCompare(b));
  };

  const buildRowsFromServerData = (serverRows: AdminPanelServerRow[], machineCode: string, dynamicSkuColumns: string[]): ConfigurationRow[] => {
    if (!serverRows || !Array.isArray(serverRows) || serverRows.length === 0) {
      return [];
    }

    const buildSkuCellValue = (row: AdminPanelServerRow): string => {
      const threshold = row.threshhold;
      const idealValue = row.ideal_value;

      const hasThreshold = threshold !== null && threshold !== undefined && String(threshold).trim() !== '';
      const hasIdealValue = idealValue !== null && idealValue !== undefined && String(idealValue).trim() !== '';

      if (hasThreshold && hasIdealValue) {
        return `${String(threshold).trim()}-${String(idealValue).trim()}`;
      }

      if (hasThreshold) {
        return String(threshold).trim();
      }

      if (hasIdealValue) {
        return `-${String(idealValue).trim()}`;
      }

      return '';
    };

    // Group rows by parameter_name
    const groupedRows: Record<string, AdminPanelServerRow[]> = {};
    serverRows.forEach((row, index) => {
      const parameterName = (row.parameter_name || row.parameter || '').trim();
      const key = parameterName || `__empty_parameter_${index}`;

      if (!groupedRows[key]) {
        groupedRows[key] = [];
      }
      groupedRows[key].push(row);
    });

    const rows: ConfigurationRow[] = Object.entries(groupedRows).map(([groupKey, parameterRows], index) => {
      const metaRow = parameterRows[0];

      const values: Record<string, string> = {};
      const skuServerRowIds: Record<string, string> = {};

      // Initialize all SKU columns with empty values
      dynamicSkuColumns.forEach((col) => {
        values[col] = '';
        skuServerRowIds[col] = '';
      });

      // Map each row directly to its SKU column using exact string match
      parameterRows.forEach((row) => {
        const rowSku = (row.sku || '').trim();
        if (!rowSku) return;

        // Direct match: find the column that matches this SKU exactly
        const targetColumn = dynamicSkuColumns.find((col) => col === rowSku);
        if (!targetColumn) return;

        const cellValue = buildSkuCellValue(row);
        if (cellValue !== '') {
          values[targetColumn] = cellValue;
        }

        const rowId = row.id || row.row_id;
        if (rowId !== undefined && rowId !== null && String(rowId).trim() !== '') {
          skuServerRowIds[targetColumn] = String(rowId);
        }
      });

      const parameterLabel = (metaRow?.parameter_name || metaRow?.parameter || '').trim() || (groupKey.startsWith('__empty_parameter_') ? '' : groupKey);
      const rowId = String(metaRow?.id || metaRow?.row_id || `row-${index + 1}`);

      return {
        id: `row-${rowId}-${index + 1}`,
        serverRowId: rowId,
        header: index === 0 ? machineCode : '',
        parameter: formatParameterForDisplay(parameterLabel),
        goldenSettingRange: '',
        values,
        skuServerRowIds,
      };
    });

    return rows;
  };

  const line1MachineOptions = machineOptions.filter((option) => normalizeLineCode(option.lineCode) === 'line1');
  const line2MachineOptions = machineOptions.filter((option) => normalizeLineCode(option.lineCode) === 'line2');

  const firstMachineKey = line1MachineOptions[0]?.key || machineOptions[0]?.key || '';
  const firstLine2MachineKey = line2MachineOptions[0]?.key || '';

  const isMeaningfulServerRow = (row: AdminPanelServerRow): boolean => {
    // A row is meaningful if it has a parameter name.
    // Threshold/ideal_value may be empty; UI should still show editable empty cells.
    const hasParameter = Boolean((row.parameter_name || row.parameter || '').toString().trim());

    return hasParameter;
  };

  const [activeTab, setActiveTab] = useState<string>(firstMachineKey);
  const [line2ActiveTab, setLine2ActiveTab] = useState<string>(firstLine2MachineKey);
  const [expandedLine, setExpandedLine] = useState<'line1' | 'line2'>('line1');
  const [tableDataByMachine, setTableDataByMachine] = useState<Record<string, ConfigurationRow[]>>({
    ...(firstMachineKey ? { [firstMachineKey]: buildRowsForMachine() } : {}),
  });
  const [editingDataByMachine, setEditingDataByMachine] = useState<Record<string, ConfigurationRow[]>>({
    ...(firstMachineKey ? { [firstMachineKey]: buildRowsForMachine() } : {}),
  });
  const [baselineDataByMachine, setBaselineDataByMachine] = useState<Record<string, ConfigurationRow[]>>({});
  const [serverDataByMachine, setServerDataByMachine] = useState<Record<string, AdminPanelServerRow[]>>({});
  const [loadedMachines, setLoadedMachines] = useState<Record<string, boolean>>({
    ...(firstMachineKey ? { [firstMachineKey]: true } : {}),
  });
  const [loadingByMachine, setLoadingByMachine] = useState<Record<string, boolean>>({});
  const [savingByMachine, setSavingByMachine] = useState<Record<string, boolean>>({});
  const [isMachinesLoading, setIsMachinesLoading] = useState<boolean>(false);
  const [notification, setNotification] = useState<NotificationState>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [skuColumnsByMachine, setSkuColumnsByMachine] = useState<Record<string, string[]>>({
    ...(firstMachineKey ? { [firstMachineKey]: defaultSkuColumns } : {}),
  });
  const lineSkuColumnsCacheRef = useRef<Record<string, string[]>>({});
  const inFlightMachineLoadsRef = useRef<Record<string, boolean>>({});
  const loadedMachineLinesRef = useRef<Record<'line1' | 'line2', boolean>>({
    line1: false,
    line2: false,
  });
  const loadingMachineLinesRef = useRef<Record<'line1' | 'line2', boolean>>({
    line1: false,
    line2: false,
  });

  const handleNotificationClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setNotification((previous) => ({
      ...previous,
      open: false,
    }));
  };

  const loadMachineOptionsForLine = async (line: 'line1' | 'line2') => {
    if (loadedMachineLinesRef.current[line] || loadingMachineLinesRef.current[line]) {
      return;
    }

    const lineId = line === 'line1' ? '1' : '2';
    loadingMachineLinesRef.current[line] = true;
    setIsMachinesLoading(true);

    try {
      const machines = await getAvailableMachinesByLine(lineId);
      setMachineOptions((previous) => {
        const merged = [...previous, ...machines];
        return Array.from(new Map(merged.map((machine) => [machine.key, machine])).values());
      });
      loadedMachineLinesRef.current[line] = true;
    } catch (error) {
      setNotification({
        open: true,
        message: `Unable to load machines for ${line === 'line1' ? 'Line 1' : 'Line 2'}.`,
        severity: 'error',
      });
    } finally {
      loadingMachineLinesRef.current[line] = false;
      setIsMachinesLoading(
        loadingMachineLinesRef.current.line1
        || loadingMachineLinesRef.current.line2
      );
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    setExpandedLine('line1');

    // Fresh lazy-load session on each dialog open: first tab is loaded by default.
    loadedMachineLinesRef.current = { line1: false, line2: false };
    loadingMachineLinesRef.current = { line1: false, line2: false };
    lineSkuColumnsCacheRef.current = {};
    inFlightMachineLoadsRef.current = {};

    setMachineOptions([]);
    setActiveTab('');
    setLine2ActiveTab('');
    setTableDataByMachine({});
    setEditingDataByMachine({});
    setBaselineDataByMachine({});
    setServerDataByMachine({});
    setSkuColumnsByMachine({});
    setLoadedMachines({});
    setLoadingByMachine({});
    setSavingByMachine({});
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (expandedLine === 'line1' || expandedLine === 'line2') {
      void loadMachineOptionsForLine(expandedLine);
    }
  }, [open, expandedLine]);

  useEffect(() => {
    if (!open || !firstMachineKey) {
      return;
    }

    if (!activeTab || !line1MachineOptions.some((option) => option.key === activeTab)) {
      setActiveTab(firstMachineKey);
      // Load only the first/active machine tab
      void loadMachineIfNeeded(firstMachineKey, false, true);
    }
  }, [open, firstMachineKey, activeTab, line1MachineOptions]);

  useEffect(() => {
    if (!open || !firstLine2MachineKey) {
      return;
    }

    if (!line2ActiveTab || !line2MachineOptions.some((option) => option.key === line2ActiveTab)) {
      setLine2ActiveTab(firstLine2MachineKey);
    }
  }, [open, firstLine2MachineKey, line2ActiveTab, line2MachineOptions]);

  useEffect(() => {
    if (!open || expandedLine !== 'line2') {
      return;
    }

    const targetMachineKey = line2ActiveTab || firstLine2MachineKey;
    if (targetMachineKey) {
      void loadMachineIfNeeded(targetMachineKey, false);
    }
  }, [open, expandedLine, line2ActiveTab, firstLine2MachineKey]);

  const loadMachineIfNeeded = async (
    machineKey: string,
    forceReload = false,
    showLoadingIndicator = true,
  ) => {
    if (inFlightMachineLoadsRef.current[machineKey]) {
      return;
    }

    if (!forceReload && loadedMachines[machineKey]) {
      return;
    }

    inFlightMachineLoadsRef.current[machineKey] = true;

    const backendEnabled = isAdminPanelConfigApiEnabled();

    if (!backendEnabled) {
      const machineRows = buildRowsForMachine();

      setTableDataByMachine((previous) => ({
        ...previous,
        [machineKey]: machineRows,
      }));
      setEditingDataByMachine((previous) => ({
        ...previous,
        [machineKey]: machineRows,
      }));
      setSkuColumnsByMachine((previous) => ({
        ...previous,
        [machineKey]: defaultSkuColumns,
      }));
      setLoadedMachines((previous) => ({
        ...previous,
        [machineKey]: true,
      }));
      setLoadingByMachine((previous) => ({
        ...previous,
        [machineKey]: false,
      }));

      inFlightMachineLoadsRef.current[machineKey] = false;

      return;
    }

    if (showLoadingIndicator) {
      setLoadingByMachine((previous) => ({
        ...previous,
        [machineKey]: true,
      }));
    }

    try {
      // Get the machine code from the machineOptions list
      const machine = machineOptions.find((m) => m.key === machineKey);
      if (!machine) {
        return;
      }

      const serverRows = await getAdminPanelMachineConfiguration(machineKey, machine.code, machine.lineCode);
      
      const meaningfulRows = serverRows.filter(isMeaningfulServerRow);
      
      const machineCode = machine.code;
      const ownMachineSkuColumns = extractSkuColumnsFromServerData(serverRows);
      // Each machine uses only its own SKU columns - no cross-machine sharing
      const dynamicSkuColumns = ownMachineSkuColumns;
      
      const rowsToUse = meaningfulRows.length > 0
        ? buildRowsFromServerData(meaningfulRows, machineCode, dynamicSkuColumns)
        : [];

      setSkuColumnsByMachine((previous) => ({
        ...previous,
        [machineKey]: dynamicSkuColumns,
      }));
      setTableDataByMachine((previous) => ({
        ...previous,
        [machineKey]: rowsToUse,
      }));
      setEditingDataByMachine((previous) => ({
        ...previous,
        [machineKey]: rowsToUse,
      }));
      setBaselineDataByMachine((previous) => ({
        ...previous,
        [machineKey]: rowsToUse,
      }));
      setServerDataByMachine((previous) => ({
        ...previous,
        [machineKey]: serverRows,
      }));
      setLoadedMachines((previous) => ({
        ...previous,
        [machineKey]: true,
      }));
    } catch (error) {
      // Fallback to base rows so UI remains functional.
      const fallbackRows = buildRowsForMachine();

      setTableDataByMachine((previous) => ({
        ...previous,
        [machineKey]: fallbackRows,
      }));
      setEditingDataByMachine((previous) => ({
        ...previous,
        [machineKey]: fallbackRows,
      }));
      setSkuColumnsByMachine((previous) => ({
        ...previous,
        [machineKey]: defaultSkuColumns,
      }));
      setLoadedMachines((previous) => ({
        ...previous,
        [machineKey]: true,
      }));
    } finally {
      setLoadingByMachine((previous) => ({
        ...previous,
        [machineKey]: false,
      }));
      inFlightMachineLoadsRef.current[machineKey] = false;
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    const revertMachineKey = activeTab;
    setEditingDataByMachine((previous) => ({
      ...previous,
      [revertMachineKey]: baselineDataByMachine[revertMachineKey]
        || tableDataByMachine[revertMachineKey]
        || previous[revertMachineKey]
        || [],
    }));

    // Lazy-load only once per machine unless explicitly forced (e.g., after save).
    void loadMachineIfNeeded(newValue, false);

    setActiveTab(newValue);
  };

  const handleLine2TabChange = (_event: React.SyntheticEvent, newValue: string) => {
    const revertMachineKey = line2ActiveTab;
    setEditingDataByMachine((previous) => ({
      ...previous,
      [revertMachineKey]: baselineDataByMachine[revertMachineKey]
        || tableDataByMachine[revertMachineKey]
        || previous[revertMachineKey]
        || [],
    }));

    // Lazy-load only once per machine unless explicitly forced (e.g., after save).
    void loadMachineIfNeeded(newValue, false);
    setLine2ActiveTab(newValue);
  };

  const handleEditableCellChange = (machineKey: string, rowId: string, columnKey: string, value: string) => {
    setEditingDataByMachine((previous) => ({
      ...previous,
      [machineKey]: (previous[machineKey] || []).map((row) =>
        row.id === rowId
          ? {
              ...row,
              values: {
                ...row.values,
                [columnKey]: value,
              },
            }
          : row
      ),
    }));
  };

  const parseMinMaxValue = (value: string): { min: string; max: string } => {
    const normalized = (value || '').trim();
    if (!normalized) {
      return { min: '', max: '' };
    }

    const cleaned = normalized.replace(/\([^)]*\)/g, '').trim();
    const dashIndex = cleaned.indexOf('-');

    if (dashIndex === -1) {
      return { min: cleaned, max: '' };
    }

    return {
      min: cleaned.slice(0, dashIndex).trim(),
      max: cleaned.slice(dashIndex + 1).trim(),
    };
  };

  const decimalNumberRegex = /^\d*\.?\d*$/;

  // Allows decimal numbers (e.g., 4, 4.5, .5) or empty string.
  // Empty values will be converted to null when saved to DB.
  const normalizeNumericInput = (value: string): string | null => {
    const normalized = String(value || '').replace(/\s+/g, '');
    // Empty string is valid (regex allows 0+ digits with optional decimal point).
    // When user clears a field and updates, it will be converted to 0 during payload mapping.
    return decimalNumberRegex.test(normalized) ? normalized : null;
  };

  const combineMinMaxValue = (min: string, max: string): string => {
    const trimmedMin = (min || '').trim();
    const trimmedMax = (max || '').trim();

    // Keep min/max position stable in UI inputs.
    // Example: min='', max='10' should be stored as '-10' (not '10'),
    // otherwise parser treats '10' as min and shifts value to min box.
    if (trimmedMin || trimmedMax) {
      return `${trimmedMin}-${trimmedMax}`;
    }

    return '';
  };

  const handleSkuMinMaxChange = (
    machineKey: string,
    rowId: string,
    columnKey: string,
    field: 'min' | 'max',
    fieldValue: string
  ) => {
    const sanitizedFieldValue = normalizeNumericInput(fieldValue);
    if (sanitizedFieldValue === null) {
      return;
    }

    const currentValue = (editingDataByMachine[machineKey] || [])
      .find((row) => row.id === rowId)?.values[columnKey] || '';
    const parsed = parseMinMaxValue(currentValue);
    const nextMin = field === 'min' ? sanitizedFieldValue : parsed.min;
    const nextMax = field === 'max' ? sanitizedFieldValue : parsed.max;

    handleEditableCellChange(machineKey, rowId, columnKey, combineMinMaxValue(nextMin, nextMax));
  };

  const isSkuCellEditable = (machineKey: string, _uiRow: ConfigurationRow, _skuKey: string): boolean => {
    const originalServerRows = serverDataByMachine[machineKey] || [];
    if (originalServerRows.length === 0) {
      return false;
    }

    // Keep all SKU fields editable once machine data is available.
    // Exact SKU row resolution is handled during save.
    return true;
  };

  const getChangedRowsForMachine = (machineKey: string): AdminPanelServerRow[] => {
    const baselineUIRows = baselineDataByMachine[machineKey] || [];
    const editedUIRows = editingDataByMachine[machineKey] || [];
    const originalServerRows = serverDataByMachine[machineKey] || [];

    const toNumberOrNull = (value: string): number | null => {
      const normalized = (value || '').trim();
      if (!normalized) return null;
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const parseMinMaxValue = (value: string): { min: string; max: string } => {
      const normalized = (value || '').trim();
      if (!normalized) return { min: '', max: '' };
      const cleaned = normalized.replace(/\([^)]*\)/g, '').trim();
      const dashIndex = cleaned.indexOf('-');
      if (dashIndex === -1) return { min: cleaned, max: '' };
      return {
        min: cleaned.slice(0, dashIndex).trim(),
        max: cleaned.slice(dashIndex + 1).trim(),
      };
    };

    const normalizeParameterKey = (value: string | undefined | null): string =>
      (value || '')
        .toLowerCase()
        .replace(/[_\-\s]+/g, '')
        .trim();

    const serverById = new Map<string, AdminPanelServerRow>();
    const serverByParameterSku = new Map<string, AdminPanelServerRow>();
    originalServerRows.forEach((row) => {
      const id = String(row.id || row.row_id || '').trim();
      if (id) {
        serverById.set(id, row);
      }

      // Use trimmed lowercase SKU for lookup key (exact match, no character stripping)
      const paramPart = normalizeParameterKey(row.parameter_name || row.parameter);
      const skuPart = (row.sku || '').trim().toLowerCase();
      const key = `${paramPart}::${skuPart}`;
      if (key !== '::') {
        serverByParameterSku.set(key, row);
      }
    });

    const resolveAllRowForUi = (uiRow: ConfigurationRow): AdminPanelServerRow | undefined => {
      const uiId = String(uiRow.id || '');
      const match = uiId.match(/^row-(.+?)-\d+$/);
      const serverRowId = String(match?.[1] || '').trim();

      if (serverRowId && serverById.has(serverRowId)) {
        return serverById.get(serverRowId);
      }

      const paramKey = normalizeParameterKey(uiRow.parameter);
      const allKey = `${paramKey}::all`;
      if (serverByParameterSku.has(allKey)) {
        return serverByParameterSku.get(allKey);
      }

      // Final fallback: any row for that parameter
      return originalServerRows.find(
        (row) => normalizeParameterKey(row.parameter_name || row.parameter) === paramKey
      );
    };

    const resolveSkuRowForUi = (uiRow: ConfigurationRow, skuColumn: string): AdminPanelServerRow | undefined => {
      // 1. Use the stored server row ID for this SKU column (most reliable)
      const mappedId = String(uiRow.skuServerRowIds?.[skuColumn] || '').trim();
      if (mappedId && serverById.has(mappedId)) {
        return serverById.get(mappedId);
      }

      // 2. Look up by parameter + SKU exact match
      const paramKey = normalizeParameterKey(uiRow.parameter);
      const lookupKey = `${paramKey}::${(skuColumn || '').trim().toLowerCase()}`;
      const exact = serverByParameterSku.get(lookupKey);
      if (exact) {
        return exact;
      }

      // 3. Fallback: find server row matching parameter and SKU string directly
      return originalServerRows.find((row) => {
        const sameParameter = normalizeParameterKey(row.parameter_name || row.parameter) === paramKey;
        const samesku = (row.sku || '').trim() === skuColumn;
        return sameParameter && samesku;
      });
    };

    const changedRows: AdminPanelServerRow[] = [];
    const addOrReplaceChangedRow = (row: AdminPanelServerRow) => {
      const rowKey = String(row.id || row.row_id || '');
      if (!rowKey) {
        changedRows.push(row);
        return;
      }

      const existingIndex = changedRows.findIndex(
        (item) => String(item.id || item.row_id || '') === rowKey
      );

      if (existingIndex >= 0) {
        changedRows[existingIndex] = {
          ...changedRows[existingIndex],
          ...row,
        };
        return;
      }

      changedRows.push(row);
    };

    editedUIRows.forEach((uiRow) => {
      const baselineRow = baselineUIRows.find((br) => br.id === uiRow.id);
      if (!baselineRow) {
        return;
      }

      // Check if ANYTHING changed
      const hasGoldenRangeChanged = uiRow.goldenSettingRange !== baselineRow.goldenSettingRange;
      const hasSkuValuesChanged = Object.keys(uiRow.values || {}).some(
        (skuKey) => (uiRow.values[skuKey] || '') !== (baselineRow.values[skuKey] || '')
      );

      if (!hasGoldenRangeChanged && !hasSkuValuesChanged) {
        return;
      }

      const originalRow = resolveAllRowForUi(uiRow);

      // 1. Handle main golden range changes (for "all" SKU row)
      if (hasGoldenRangeChanged && originalRow) {
        const parsed = parseMinMaxValue(uiRow.goldenSettingRange || '');
        const updateRow: AdminPanelServerRow = {
          id: originalRow.id,
          row_id: originalRow.row_id,
          parameter_name: originalRow.parameter_name,
          sku: originalRow.sku,
          machine_code: originalRow.machine_code,
          line_code: originalRow.line_code,
          units: originalRow.units,
          threshhold: toNumberOrNull(parsed.min),
          ideal_value: toNumberOrNull(parsed.max),
        };

        addOrReplaceChangedRow(updateRow);
      }

      // 2. Handle SKU-specific value changes
      if (hasSkuValuesChanged) {
        Object.keys(uiRow.values || {}).forEach((skuColumn) => {
          const editedValue = uiRow.values[skuColumn] || '';
          const baselineValue = baselineRow.values[skuColumn] || '';

          if (editedValue !== baselineValue) {
            const skuOriginalRow = resolveSkuRowForUi(uiRow, skuColumn);

            if (!skuOriginalRow) {
              return;
            }

            const parsed = parseMinMaxValue(editedValue);
            const skuUpdateRow: AdminPanelServerRow = {
              id: skuOriginalRow.id,
              row_id: skuOriginalRow.row_id,
              parameter_name: skuOriginalRow.parameter_name,
              sku: skuOriginalRow.sku,
              machine_code: skuOriginalRow.machine_code,
              line_code: skuOriginalRow.line_code,
              units: skuOriginalRow.units,
              threshhold: toNumberOrNull(parsed.min),
              ideal_value: toNumberOrNull(parsed.max),
            };
            addOrReplaceChangedRow(skuUpdateRow);
          }
        });
      }
    });
    
    return changedRows;
  };

  /**
   * Build snapshot_records: ALL rows for every SKU that had at least one change,
   * with the new (updated) values already applied to the changed rows.
   */
  const getSnapshotRecordsForMachine = (machineKey: string, changedRows: AdminPanelServerRow[]): AdminPanelServerRow[] => {
    const originalServerRows = serverDataByMachine[machineKey] || [];

    if (changedRows.length === 0 || originalServerRows.length === 0) {
      return [];
    }

    // Collect unique SKUs that have been modified (including "all" for machines like Posimat)
    const changedSkus = new Set<string>();
    changedRows.forEach((row) => {
      const sku = (row.sku || '').trim().toLowerCase();
      if (sku) {
        changedSkus.add(sku);
      }
    });

    if (changedSkus.size === 0) {
      return [];
    }

    // Build a map of changed rows keyed by id for quick lookup
    const changedById = new Map<string, AdminPanelServerRow>();
    changedRows.forEach((row) => {
      const rowId = String(row.id || row.row_id || '').trim();
      if (rowId) {
        changedById.set(rowId, row);
      }
    });

    const snapshotRecords: AdminPanelServerRow[] = [];

    // For each server row belonging to a touched SKU, include it in the snapshot
    // with updated values applied if it was changed.
    originalServerRows.forEach((serverRow) => {
      const serverSku = (serverRow.sku || '').trim().toLowerCase();
      if (!changedSkus.has(serverSku)) {
        return;
      }

      const rowId = String(serverRow.id || serverRow.row_id || '').trim();
      const changedVersion = rowId ? changedById.get(rowId) : undefined;

      if (changedVersion) {
        // Use the updated values for this row
        snapshotRecords.push({
          id: changedVersion.id,
          row_id: changedVersion.row_id,
          line_code: changedVersion.line_code || serverRow.line_code,
          machine_code: changedVersion.machine_code || serverRow.machine_code,
          sku: changedVersion.sku || serverRow.sku,
          parameter_name: changedVersion.parameter_name || serverRow.parameter_name,
          ideal_value: changedVersion.ideal_value,
          threshhold: changedVersion.threshhold,
          units: changedVersion.units || serverRow.units,
        });
      } else {
        // Include unchanged row with its current server values
        snapshotRecords.push({
          id: serverRow.id,
          row_id: serverRow.row_id,
          line_code: serverRow.line_code,
          machine_code: serverRow.machine_code,
          sku: serverRow.sku,
          parameter_name: serverRow.parameter_name,
          ideal_value: serverRow.ideal_value !== null && serverRow.ideal_value !== undefined
            ? Number(serverRow.ideal_value)
            : null,
          threshhold: serverRow.threshhold !== null && serverRow.threshhold !== undefined
            ? Number(serverRow.threshhold)
            : null,
          units: serverRow.units,
        });
      }
    });

    return snapshotRecords;
  };

  const hasUnsavedChangesForMachine = (machineKey: string): boolean => {
    const baselineUIRows = baselineDataByMachine[machineKey] || [];
    const editedUIRows = editingDataByMachine[machineKey] || [];

    if (baselineUIRows.length !== editedUIRows.length) {
      return true;
    }

    for (const uiRow of editedUIRows) {
      const baselineRow = baselineUIRows.find((br) => br.id === uiRow.id);
      if (!baselineRow) {
        return true;
      }

      if ((uiRow.goldenSettingRange || '') !== (baselineRow.goldenSettingRange || '')) {
        return true;
      }

      const skuKeys = new Set<string>([
        ...Object.keys(uiRow.values || {}),
        ...Object.keys(baselineRow.values || {}),
      ]);

      for (const skuKey of skuKeys) {
        if ((uiRow.values?.[skuKey] || '') !== (baselineRow.values?.[skuKey] || '')) {
          return true;
        }
      }
    }

    return false;
  };

  const handleUpdate = async (machineKey: string) => {
    if (!editingDataByMachine[machineKey]) {
      return;
    }

    const machine = machineOptions.find((m) => m.key === machineKey);
    if (!machine) {
      return;
    }

    const changedRows = getChangedRowsForMachine(machineKey);

    if (changedRows.length === 0) {
      const hasUnsavedChanges = hasUnsavedChangesForMachine(machineKey);
      setNotification({
        open: true,
        message: hasUnsavedChanges
          ? 'Changed cells could not be mapped to database rows. Please refresh and try again.'
          : 'No changes to save.',
        severity: hasUnsavedChanges ? 'error' : 'info',
      });
      return;
    }

    // Build snapshot_records: all rows for touched SKUs with new values applied
    const snapshotRecords = getSnapshotRecordsForMachine(machineKey, changedRows);

    // Set saving state
    setSavingByMachine((previous) => ({
      ...previous,
      [machineKey]: true,
    }));

    try {
      await saveAdminPanelConfiguration(machine.code, changedRows, undefined, undefined, machine.lineCode, snapshotRecords);

      // Verify persistence by re-fetching from backend and comparing changed rows.
      // Keep this best-effort: backend may upsert rows without preserving request keys for new SKU payloads.
      const normalizeKey = (value: string | undefined | null): string =>
        (value || '').toString().trim().toLowerCase();
      const normalizeSkuKey = (value: string | undefined | null): string =>
        (value || '').toString().trim().toLowerCase().replace(/\s+/g, '');
      const normalizeParameterKey = (value: string | undefined | null): string =>
        (value || '').toString().trim().toLowerCase().replace(/[_\-\s]+/g, '');
      const toNullableNumber = (value: unknown): number | null => {
        if (value === null || value === undefined || String(value).trim() === '') {
          return null;
        }

        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      };
      const hasRowId = (value: unknown): boolean => value !== undefined && value !== null && String(value).trim() !== '';
      const areNumbersDifferent = (a: number | null, b: number | null): boolean => {
        if (a === null || b === null) {
          return a !== b;
        }

        return Math.abs(a - b) > 1e-9;
      };
      const sleep = (ms: number) => new Promise((resolve) => {
        setTimeout(resolve, ms);
      });

      let refreshedRows = await getAdminPanelMachineConfiguration(
        machineKey,
        machine.code,
        machine.lineCode
      );

      let nonPersistedRows = changedRows.filter((changedRow) => {
        const changedId = changedRow.id || changedRow.row_id;
        const rowHasId = hasRowId(changedId);

        let persistedRow = refreshedRows.find((row) => {
          const rowId = row.id || row.row_id;

          if (rowHasId) {
            return String(rowId || '') === String(changedId);
          }

          return (
            normalizeKey(row.machine_code) === normalizeKey(changedRow.machine_code || machine.code)
            && normalizeKey(row.line_code) === normalizeKey(changedRow.line_code || machine.lineCode)
            && normalizeParameterKey(row.parameter_name || row.parameter) === normalizeParameterKey(changedRow.parameter_name || changedRow.parameter)
            && normalizeSkuKey(row.sku) === normalizeSkuKey(changedRow.sku)
          );
        });

        // For id-less insert/upsert payloads, do not fail verification when no exact row can be re-matched.
        if (!persistedRow) {
          return rowHasId;
        }

        const changedThreshold = toNullableNumber(changedRow.threshhold);
        const changedMax = toNullableNumber(changedRow.ideal_value);
        const persistedThreshold = toNullableNumber(persistedRow.threshhold);
        const persistedMax = toNullableNumber(persistedRow.ideal_value);

        const thresholdMismatch = areNumbersDifferent(changedThreshold, persistedThreshold);
        const maxMismatch = areNumbersDifferent(changedMax, persistedMax);

        // If both changed and persisted are null, it's a successful clear operation
        const isClearOperation = changedThreshold === null && changedMax === null && persistedThreshold === null && persistedMax === null;

        // Treat null-to-null as successful (don't fail on clear operations)
        return (thresholdMismatch || maxMismatch) && !isClearOperation;
      });

      // Retry once for eventual-consistency backends before logging mismatch.
      if (nonPersistedRows.length > 0) {
        await sleep(300);
        refreshedRows = await getAdminPanelMachineConfiguration(
          machineKey,
          machine.code,
          machine.lineCode
        );

        nonPersistedRows = changedRows.filter((changedRow) => {
          const changedId = changedRow.id || changedRow.row_id;
          const rowHasId = hasRowId(changedId);

          let persistedRow = refreshedRows.find((row) => {
            const rowId = row.id || row.row_id;

            if (rowHasId) {
              return String(rowId || '') === String(changedId);
            }

            return (
              normalizeKey(row.machine_code) === normalizeKey(changedRow.machine_code || machine.code)
              && normalizeKey(row.line_code) === normalizeKey(changedRow.line_code || machine.lineCode)
              && normalizeParameterKey(row.parameter_name || row.parameter) === normalizeParameterKey(changedRow.parameter_name || changedRow.parameter)
              && normalizeSkuKey(row.sku) === normalizeSkuKey(changedRow.sku)
            );
          });

          if (!persistedRow) {
            return rowHasId;
          }

          const changedThreshold = toNullableNumber(changedRow.threshhold);
          const changedMax = toNullableNumber(changedRow.ideal_value);
          const persistedThreshold = toNullableNumber(persistedRow.threshhold);
          const persistedMax = toNullableNumber(persistedRow.ideal_value);

          const thresholdMismatch = areNumbersDifferent(changedThreshold, persistedThreshold);
          const maxMismatch = areNumbersDifferent(changedMax, persistedMax);

          // If both changed and persisted are null, it's a successful clear operation
          const isClearOperation = changedThreshold === null && changedMax === null && persistedThreshold === null && persistedMax === null;

          // Treat null-to-null as successful (don't fail on clear operations)
          return (thresholdMismatch || maxMismatch) && !isClearOperation;
        });
      }

      // Backend compatibility fallback:
      // Some backends ignore JSON null for numeric columns but accept empty-string clear intent.
      if (nonPersistedRows.length > 0) {
        const clearRows = nonPersistedRows.filter((row) => {
          const threshold = toNullableNumber(row.threshhold);
          const max = toNullableNumber(row.ideal_value);
          return threshold === null && max === null;
        });

        if (clearRows.length > 0) {
          await saveAdminPanelConfiguration(machine.code, clearRows, undefined, {
            useEmptyStringForNullClear: true,
          }, machine.lineCode);

          await sleep(300);
          refreshedRows = await getAdminPanelMachineConfiguration(
            machineKey,
            machine.code,
            machine.lineCode
          );

          nonPersistedRows = changedRows.filter((changedRow) => {
            const changedId = changedRow.id || changedRow.row_id;
            const rowHasId = hasRowId(changedId);

            const persistedRow = refreshedRows.find((row) => {
              const rowId = row.id || row.row_id;

              if (rowHasId) {
                return String(rowId || '') === String(changedId);
              }

              return (
                normalizeKey(row.machine_code) === normalizeKey(changedRow.machine_code || machine.code)
                && normalizeKey(row.line_code) === normalizeKey(changedRow.line_code || machine.lineCode)
                && normalizeParameterKey(row.parameter_name || row.parameter) === normalizeParameterKey(changedRow.parameter_name || changedRow.parameter)
                && normalizeSkuKey(row.sku) === normalizeSkuKey(changedRow.sku)
              );
            });

            if (!persistedRow) {
              return rowHasId;
            }

            const changedThreshold = toNullableNumber(changedRow.threshhold);
            const changedMax = toNullableNumber(changedRow.ideal_value);
            const persistedThreshold = toNullableNumber(persistedRow.threshhold);
            const persistedMax = toNullableNumber(persistedRow.ideal_value);

            const thresholdMismatch = areNumbersDifferent(changedThreshold, persistedThreshold);
            const maxMismatch = areNumbersDifferent(changedMax, persistedMax);
            const isClearOperation = changedThreshold === null && changedMax === null && persistedThreshold === null && persistedMax === null;
            return (thresholdMismatch || maxMismatch) && !isClearOperation;
          });
        }
      }

      if (nonPersistedRows.length > 0) {
        setNotification({
          open: true,
          message: `Persistence verification failed for ${nonPersistedRows.length} row(s). Check console for details.`,
          severity: 'warning',
        });
      }
      
      // Only show success and update UI if verification passed
      if (nonPersistedRows.length === 0) {
        setNotification({
          open: true,
          message: 'Changes saved successfully.',
          severity: 'success',
        });
      }

      const meaningfulRefreshedRows = refreshedRows.filter(isMeaningfulServerRow);
      const ownMachineSkuColumns = extractSkuColumnsFromServerData(refreshedRows);
      // Each machine uses only its own SKU columns
      const dynamicSkuColumns = ownMachineSkuColumns;

      const refreshedUiRows = meaningfulRefreshedRows.length > 0
        ? buildRowsFromServerData(meaningfulRefreshedRows, machine.code, dynamicSkuColumns)
        : [];

      setSkuColumnsByMachine((previous) => ({
        ...previous,
        [machineKey]: dynamicSkuColumns,
      }));
      setTableDataByMachine((previous) => ({
        ...previous,
        [machineKey]: refreshedUiRows,
      }));
      setEditingDataByMachine((previous) => ({
        ...previous,
        [machineKey]: refreshedUiRows,
      }));
      setBaselineDataByMachine((previous) => ({
        ...previous,
        [machineKey]: refreshedUiRows,
      }));
      setServerDataByMachine((previous) => ({
        ...previous,
        [machineKey]: refreshedRows,
      }));
      setLoadedMachines((previous) => ({
        ...previous,
        [machineKey]: true,
      }));

      // Release button loading state immediately after successful save.
      setSavingByMachine((previous) => ({
        ...previous,
        [machineKey]: false,
      }));
    } catch (error) {
      const backendDetail = (error as any)?.details?.data?.detail;
      const backendMessage = (error as any)?.details?.data?.message;
      const errorMessage = backendDetail || backendMessage || 'Failed to save changes. Please try again.';
      setNotification({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setSavingByMachine((previous) => ({
        ...previous,
        [machineKey]: false,
      }));
    }
  };

  const activeMachineOption = line1MachineOptions.find((option) => option.key === activeTab) || line1MachineOptions[0] || machineOptions[0];
  const line2ActiveMachineOption = line2MachineOptions.find((option) => option.key === line2ActiveTab) || line2MachineOptions[0];
  const safeLine1TabValue: string | false = line1MachineOptions.some((option) => option.key === activeTab)
    ? activeTab
    : (line1MachineOptions[0]?.key || false);
  const safeLine2TabValue: string | false = line2MachineOptions.some((option) => option.key === line2ActiveTab)
    ? line2ActiveTab
    : (line2MachineOptions[0]?.key || false);
  if (!activeMachineOption) {
    // Backend-only machines: show loading until fetch resolves, then show explicit empty state.
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          {isMachinesLoading ? (
            <CircularProgress />
          ) : (
            <Alert severity="warning" sx={{ width: '100%', maxWidth: '600px' }}>
              No machines returned by backend. Please verify /admin/machines response.
            </Alert>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  const getDisplaySkuColumnsForMachine = (machine: MachineOption | undefined): string[] => {
    if (!machine) {
      return [];
    }

    // Return ONLY the current machine's own SKU columns (not aggregated from other machines)
    const machineSpecificColumns = skuColumnsByMachine[machine.key] || [];
    return machineSpecificColumns;
  };

  const activeRows = editingDataByMachine[activeMachineOption.key] || [];
  const isActiveTabLoading = Boolean(loadingByMachine[activeMachineOption.key]);
  const hasActiveTabChanges = hasUnsavedChangesForMachine(activeMachineOption.key);

  // Helper to check if a machine is a serac machine (serac only shows Ideal Value, no Threshold)
  const isSeracMachine = (machine: MachineOption | undefined): boolean => {
    if (!machine) return false;
    const code = (machine.code || '').toLowerCase();
    return code.includes('serac');
  };

  const isActiveSerac = isSeracMachine(activeMachineOption);
  
  // Build dynamic table headers based on the active machine's SKU columns
  const dynamicSkuColumns = getDisplaySkuColumnsForMachine(activeMachineOption);
  const dynamicTableHeaders = [
    'Parameter',
    ...dynamicSkuColumns,
  ];
  // Serac machines only have 1 column per SKU (Ideal Value), others have 2 (Threshold + Ideal Value)
  const totalTableColumns = 1 + (dynamicTableHeaders.length - 1) * (isActiveSerac ? 1 : 2);

  const line2ActiveRows = line2ActiveMachineOption ? (editingDataByMachine[line2ActiveMachineOption.key] || []) : [];
  const isLine2ActiveTabLoading = line2ActiveMachineOption ? Boolean(loadingByMachine[line2ActiveMachineOption.key]) : false;
  const hasLine2ActiveTabChanges = line2ActiveMachineOption
    ? hasUnsavedChangesForMachine(line2ActiveMachineOption.key)
    : false;
  const isLine2ActiveSerac = isSeracMachine(line2ActiveMachineOption);
  const line2DynamicSkuColumns = getDisplaySkuColumnsForMachine(line2ActiveMachineOption);
  const line2TableHeaders = [
    'Parameter',
    ...line2DynamicSkuColumns,
  ];
  const line2TotalColumns = 1 + (line2TableHeaders.length - 1) * (isLine2ActiveSerac ? 1 : 2);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '95vw',
          height: '90vh',
          maxWidth: '1500px',
          bgcolor: '#192033',
          backgroundImage: 'none',
          borderRadius: '8px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0.75,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            borderBottom: '1px solid #334155',
          }}
        >
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 600,
              color: '#ffffff',
              letterSpacing: '0.5px',
            }}
          >
            Admin Panel
          </Typography>

          <IconButton
            onClick={onClose}
            aria-label="close"
            sx={{
              color: '#ffffff',
              '&:hover': {
                color: '#ffffff',
                bgcolor: '#334155',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
            minHeight: 0,
            overflow: 'hidden',
            bgcolor: '#192033',
            p: 0.5,
          }}
        >
          <Accordion
            expanded={expandedLine === 'line1'}
            onChange={(_event, isExpanded) => {
              setExpandedLine(isExpanded ? 'line1' : 'line2');
            }}
            disableGutters
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              ...(expandedLine === 'line1' && {
                flex: 1,
                height: '100%',
              }),
              ...(!expandedLine.includes('line1') && {
                maxHeight: '40px',
                minHeight: '40px',
              }),
              bgcolor: '#0f172a',
              color: '#ffffff',
              border: '1px solid #334155',
              borderRadius: '6px',
              m: 0,
              mb: 0,
              '&.Mui-expanded': {
                m: 0,
                mb: 0,
              },
              '& .MuiCollapse-root': {
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              },
              '& .MuiCollapse-wrapper': {
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              },
              '& .MuiCollapse-wrapperInner': {
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              },
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: '#cbd5e1' }} />}
              sx={{
                minHeight: '40px',
                px: 1,
                bgcolor: '#1f2937',
                borderBottom: '1px solid #334155',
                '& .MuiAccordionSummary-content': {
                  my: 0.5,
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#ffffff',
                  letterSpacing: '0.3px',
                }}
              >
                Line 1
              </Typography>
            </AccordionSummary>

            <AccordionDetails
              sx={{
                p: 0.5,
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                overflow: 'hidden',
              }}
            >
              <Tabs
                value={safeLine1TabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                textColor="inherit"
                sx={{
                  minHeight: '36px',
                  flexShrink: 0,
                  mb: 0,
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#46A2FE',
                    height: '3px',
                  },
                  '& .MuiTabs-scrollButtons': {
                    color: '#cbd5e1',
                  },
                }}
              >
                {line1MachineOptions.map((option) => (
                  <Tab
                    key={option.key}
                    value={option.key}
                    label={option.label}
                    sx={{
                      color: '#cbd5e1',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '12px',
                      minHeight: '36px',
                      '&.Mui-selected': {
                        color: '#46A2FE',
                      },
                    }}
                  />
                ))}
              </Tabs>

              <TableContainer
                sx={{
                  flex: '0 1 auto',
                  minHeight: '180px',
                  maxHeight: '45vh',
                  position: 'relative',
                  border: '1px solid #334155',
                  borderRadius: '4px',
                  bgcolor: '#0f172a',
                  overflowX: 'auto',
                  overflowY: 'auto',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#64748b #0b1220',
                  '&::-webkit-scrollbar': {
                    height: '10px',
                    width: '10px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#0b1220',
                    borderRadius: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#64748b',
                    borderRadius: '8px',
                    border: '2px solid #0b1220',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: '#94a3b8',
                  },
                  boxShadow: 'inset 0 0 0 1px rgba(51, 65, 85, 0.2)',
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: '920px',
                    width: 'auto',
                    tableLayout: 'auto',
                    '& .MuiTableCell-root': {
                      padding: '6px 8px !important',
                      verticalAlign: 'middle',
                    },
                  }}
                >
                      <TableHead>
                        <TableRow>
                          {dynamicTableHeaders.map((header, headerIndex) => (
                            <TableCell
                              key={header}
                              colSpan={headerIndex === 0 ? 1 : (isActiveSerac ? 1 : 2)}
                              sx={{
                                bgcolor: theme.palette.mode === 'dark' ? '#1f2937' : '#e2e8f0',
                                color: theme.palette.mode === 'dark' ? '#f8fafc' : '#0f172a',
                                position: 'sticky',
                                top: 0,
                                zIndex: 4,
                                fontWeight: 700,
                                fontSize: '11px',
                                textAlign: 'center',
                                borderRight: headerIndex < dynamicTableHeaders.length - 1 ? '1px solid #475569' : 'none',
                                borderBottom: '1px solid #475569',
                                whiteSpace: 'normal',
                                wordBreak: 'normal',
                                overflowWrap: 'normal',
                                lineHeight: 1.1,
                                px: 0.9,
                                py: 0.6,
                                minWidth: headerIndex === 0 ? 150 : 112,
                              }}
                            >
                              {header.toLowerCase() === 'all' ? 'All SKU' : header}
                            </TableCell>
                          ))}
                        </TableRow>
                        <TableRow>
                          {dynamicTableHeaders.map((header, headerIndex) => {
                            if (headerIndex === 0) {
                              return (
                                <TableCell
                                  key={`${header}-subheader`}
                                  sx={{
                                    bgcolor: theme.palette.mode === 'dark' ? '#111827' : '#f1f5f9',
                                    color: theme.palette.mode === 'dark' ? '#cbd5e1' : '#475569',
                                    position: 'sticky',
                                    top: '32px',
                                    zIndex: 3,
                                    fontWeight: 600,
                                    fontSize: '10px',
                                    textAlign: 'center',
                                    borderRight: '1px solid #475569',
                                    borderBottom: '1px solid #475569',
                                    minWidth: 150,
                                    px: 0.9,
                                    py: 0.5,
                                  }}
                                />
                              );
                            }

                            return (
                              <React.Fragment key={`${header}-subheaders`}>
                                {!isActiveSerac && (
                                <TableCell
                                  sx={{
                                    bgcolor: theme.palette.mode === 'dark' ? '#111827' : '#f1f5f9',
                                    color: theme.palette.mode === 'dark' ? '#cbd5e1' : '#475569',
                                    position: 'sticky',
                                    top: '32px',
                                    zIndex: 3,
                                    fontWeight: 600,
                                    fontSize: '10px',
                                    textAlign: 'center',
                                    borderRight: '1px solid #475569',
                                    borderBottom: '1px solid #475569',
                                    minWidth: 56,
                                    px: 0.9,
                                    py: 0.5,
                                  }}
                                >
                                  Threshold
                                </TableCell>
                                )}
                                <TableCell
                                  sx={{
                                    bgcolor: theme.palette.mode === 'dark' ? '#111827' : '#f1f5f9',
                                    color: theme.palette.mode === 'dark' ? '#cbd5e1' : '#475569',
                                    position: 'sticky',
                                    top: '32px',
                                    zIndex: 3,
                                    fontWeight: 600,
                                    fontSize: '10px',
                                    textAlign: 'center',
                                    borderRight: headerIndex < dynamicTableHeaders.length - 1 ? '1px solid #475569' : 'none',
                                    borderBottom: '1px solid #475569',
                                    minWidth: 56,
                                    px: 0.9,
                                    py: 0.5,
                                  }}
                                >
                                  Ideal Value
                                </TableCell>
                              </React.Fragment>
                            );
                          })}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {!isActiveTabLoading && activeRows.length === 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={totalTableColumns}
                              sx={{
                                bgcolor: theme.palette.mode === 'dark' ? '#111827' : '#ffffff',
                                color: theme.palette.mode === 'dark' ? '#e5e7eb' : '#111827',
                                textAlign: 'center',
                                py: 2,
                                fontSize: '12px',
                              }}
                            >
                              No records found
                            </TableCell>
                          </TableRow>
                        )}
                        {activeRows.map((row) => (
                          <TableRow key={`${activeMachineOption.key}-${row.id}`}>
                            <TableCell
                              sx={{
                                bgcolor: theme.palette.mode === 'dark' ? '#111827' : '#ffffff',
                                color: theme.palette.mode === 'dark' ? '#e5e7eb' : '#111827',
                                fontSize: '12px',
                                fontWeight: 500,
                                borderRight: '1px solid #334155',
                                borderBottom: '1px solid #334155',
                                width: 150,
                                minWidth: 150,
                                px: 1,
                                py: 0.75,
                                whiteSpace: 'normal',
                                wordBreak: 'normal',
                                overflowWrap: 'normal',
                                lineHeight: 1.3,
                                verticalAlign: 'middle',
                              }}
                            >
                              {row.parameter}
                            </TableCell>
                            {dynamicSkuColumns.map((columnKey, columnIndex) => (
                              <React.Fragment key={`${activeMachineOption.key}-${row.id}-${columnKey}`}>
                                {!isActiveSerac && (
                                <TableCell
                                  sx={{
                                    bgcolor: theme.palette.mode === 'dark' ? '#111827' : '#ffffff',
                                    borderRight: '1px solid #334155',
                                    borderBottom: '1px solid #334155',
                                    minWidth: 56,
                                    px: 0.7,
                                    py: 0.55,
                                  }}
                                >
                                  {(() => {
                                    const canEditSku = isSkuCellEditable(activeMachineOption.key, row, columnKey);
                                    return (
                                  <Box
                                    component="input"
                                    value={parseMinMaxValue(row.values[columnKey] || '').min}
                                    placeholder="Threshold"
                                    disabled={!canEditSku}
                                    title={canEditSku ? '' : 'No DB row for this parameter+SKU'}
                                    onClick={(event: React.MouseEvent<HTMLInputElement>) => event.stopPropagation()}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                      handleSkuMinMaxChange(activeMachineOption.key, row.id, columnKey, 'min', event.target.value)
                                    }
                                    inputMode="numeric"
                                    autoComplete="off"
                                    sx={{
                                      width: '100%',
                                      height: '26px',
                                      px: 0.6,
                                      border: theme.palette.mode === 'dark' ? '1px solid #475569' : '1px solid #cbd5e1',
                                      borderRadius: '4px',
                                      fontSize: '11px',
                                      color: theme.palette.mode === 'dark' ? '#f8fafc' : '#111827',
                                      bgcolor: !canEditSku
                                        ? (theme.palette.mode === 'dark' ? '#1f2937' : '#f1f5f9')
                                        : (theme.palette.mode === 'dark' ? '#0b1220' : '#ffffff'),
                                      caretColor: theme.palette.mode === 'dark' ? '#f8fafc' : '#111827',
                                      outline: 'none',
                                      boxSizing: 'border-box',
                                      '&:focus': {
                                        borderColor: '#46A2FE',
                                        boxShadow: '0 0 0 1px #46A2FE',
                                      },
                                    }}
                                  />
                                    );
                                  })()}
                                </TableCell>
                                )}
                                <TableCell
                                  sx={{
                                    bgcolor: theme.palette.mode === 'dark' ? '#111827' : '#ffffff',
                                    borderRight: columnIndex < dynamicSkuColumns.length - 1 ? '1px solid #334155' : 'none',
                                    borderBottom: '1px solid #334155',
                                    minWidth: 56,
                                    px: 0.7,
                                    py: 0.55,
                                  }}
                                >
                                  {(() => {
                                    const canEditSku = isSkuCellEditable(activeMachineOption.key, row, columnKey);
                                    return (
                                  <Box
                                    component="input"
                                    value={parseMinMaxValue(row.values[columnKey] || '').max}
                                    placeholder="Ideal Value"
                                    disabled={!canEditSku}
                                    title={canEditSku ? '' : 'No DB row for this parameter+SKU'}
                                    onClick={(event: React.MouseEvent<HTMLInputElement>) => event.stopPropagation()}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                      handleSkuMinMaxChange(activeMachineOption.key, row.id, columnKey, 'max', event.target.value)
                                    }
                                    inputMode="numeric"
                                    autoComplete="off"
                                    sx={{
                                      width: '100%',
                                      height: '26px',
                                      px: 0.6,
                                      border: theme.palette.mode === 'dark' ? '1px solid #475569' : '1px solid #cbd5e1',
                                      borderRadius: '4px',
                                      fontSize: '11px',
                                      color: theme.palette.mode === 'dark' ? '#f8fafc' : '#111827',
                                      bgcolor: !canEditSku
                                        ? (theme.palette.mode === 'dark' ? '#1f2937' : '#f1f5f9')
                                        : (theme.palette.mode === 'dark' ? '#0b1220' : '#ffffff'),
                                      caretColor: theme.palette.mode === 'dark' ? '#f8fafc' : '#111827',
                                      outline: 'none',
                                      boxSizing: 'border-box',
                                      '&:focus': {
                                        borderColor: '#46A2FE',
                                        boxShadow: '0 0 0 1px #46A2FE',
                                      },
                                    }}
                                  />
                                    );
                                  })()}
                                </TableCell>
                              </React.Fragment>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                </Table>
                {isActiveTabLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                      zIndex: 5,
                      bgcolor: 'rgba(15, 23, 42, 0.22)',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.25,
                        py: 0.75,
                        borderRadius: '6px',
                        bgcolor: 'rgba(15, 23, 42, 0.88)',
                        border: '1px solid #334155',
                      }}
                    >
                      <CircularProgress size={16} sx={{ color: '#46A2FE' }} />
                      <Typography component="span" sx={{ fontSize: '12px', color: '#e5e7eb' }}>
                        Loading configuration...
                      </Typography>
                    </Box>
                  </Box>
                )}
              </TableContainer>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                    pt: 1,
                    px: 1,
                    pb: 0.5,
                    mt: 0.5,
                    borderTop: '1px solid #334155',
                    flexShrink: 0,
                    minHeight: '37px',
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={() => handleUpdate(activeMachineOption.key)}
                    disabled={
                      !loadedMachines[activeMachineOption.key]
                      || isActiveTabLoading
                      || (savingByMachine[activeMachineOption.key] || false)
                      || !hasActiveTabChanges
                    }
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      bgcolor: '#46A2FE',
                      '&:hover': {
                        bgcolor: '#2D8BEB',
                      },
                    }}
                  >
                    {savingByMachine[activeMachineOption.key] ? (
                      <>
                        <CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} />
                        Saving...
                      </>
                    ) : (
                      'Update'
                    )}
                  </Button>
                </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expandedLine === 'line2'}
            onChange={(_event, isExpanded) => {
              setExpandedLine(isExpanded ? 'line2' : 'line1');
            }}
            disableGutters
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              ...(expandedLine === 'line2' && {
                flex: 1,
                height: '100%',
              }),
              ...(!expandedLine.includes('line2') && {
                maxHeight: '40px',
                minHeight: '40px',
              }),
              bgcolor: '#0f172a',
              color: '#ffffff',
              border: '1px solid #334155',
              borderRadius: '6px',
              overflow: 'hidden',
              m: 0,
              mb: 0,
              '&.Mui-expanded': {
                m: 0,
                mb: 0,
              },
              '& .MuiCollapse-root': {
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              },
              '& .MuiCollapse-wrapper': {
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              },
              '& .MuiCollapse-wrapperInner': {
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              },
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: '#cbd5e1' }} />}
              sx={{
                minHeight: '40px',
                px: 1,
                bgcolor: '#1f2937',
                borderBottom: '1px solid #334155',
                '& .MuiAccordionSummary-content': {
                  my: 0.5,
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#ffffff',
                  letterSpacing: '0.3px',
                }}
              >
                Line 2
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                p: 0.5,
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                overflow: 'hidden',
              }}
            >
              <Tabs
                value={safeLine2TabValue}
                onChange={handleLine2TabChange}
                variant="scrollable"
                scrollButtons="auto"
                textColor="inherit"
                sx={{
                  minHeight: '36px',
                  flexShrink: 0,
                  mb: 0,
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#46A2FE',
                    height: '3px',
                  },
                  '& .MuiTabs-scrollButtons': {
                    color: '#cbd5e1',
                  },
                }}
              >
                {line2MachineOptions.map((option) => (
                  <Tab
                    key={option.key}
                    value={option.key}
                    label={option.label}
                    sx={{
                      color: '#cbd5e1',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '12px',
                      minHeight: '36px',
                      '&.Mui-selected': {
                        color: '#46A2FE',
                      },
                    }}
                  />
                ))}
              </Tabs>

              <TableContainer
                sx={{
                  flex: '0 1 auto',
                  minHeight: '180px',
                  maxHeight: '45vh',
                  position: 'relative',
                  border: '1px solid #334155',
                  borderRadius: '4px',
                  bgcolor: '#0f172a',
                  overflowX: 'auto',
                  overflowY: 'auto',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#64748b #0b1220',
                  '&::-webkit-scrollbar': {
                    height: '10px',
                    width: '10px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#0b1220',
                    borderRadius: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#64748b',
                    borderRadius: '8px',
                    border: '2px solid #0b1220',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: '#94a3b8',
                  },
                  boxShadow: 'inset 0 0 0 1px rgba(51, 65, 85, 0.2)',
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: '920px',
                    width: 'auto',
                    tableLayout: 'auto',
                    '& .MuiTableCell-root': {
                      padding: '6px 8px !important',
                      verticalAlign: 'middle',
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {line2TableHeaders.map((header, headerIndex) => (
                        <TableCell
                          key={`line2-${header}`}
                          colSpan={headerIndex === 0 ? 1 : (isLine2ActiveSerac ? 1 : 2)}
                          sx={{
                            bgcolor: theme.palette.mode === 'dark' ? '#1f2937' : '#e2e8f0',
                            color: theme.palette.mode === 'dark' ? '#f8fafc' : '#0f172a',
                            position: 'sticky',
                            top: 0,
                            zIndex: 4,
                            fontWeight: 700,
                            fontSize: '11px',
                            textAlign: 'center',
                            borderRight: headerIndex < line2TableHeaders.length - 1 ? '1px solid #475569' : 'none',
                            borderBottom: '1px solid #475569',
                            whiteSpace: 'normal',
                            lineHeight: 1.1,
                            px: 0.9,
                            py: 0.6,
                            minWidth: headerIndex === 0 ? 150 : 112,
                          }}
                        >
                          {header.toLowerCase() === 'all' ? 'All SKU' : header}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      {line2TableHeaders.map((header, headerIndex) => {
                        if (headerIndex === 0) {
                          return (
                            <TableCell
                              key={`line2-${header}-sub`}
                              sx={{
                                bgcolor: theme.palette.mode === 'dark' ? '#111827' : '#f1f5f9',
                                color: theme.palette.mode === 'dark' ? '#cbd5e1' : '#475569',
                                position: 'sticky',
                                top: '32px',
                                zIndex: 3,
                                fontWeight: 600,
                                fontSize: '10px',
                                textAlign: 'center',
                                borderRight: '1px solid #475569',
                                borderBottom: '1px solid #475569',
                                minWidth: 150,
                                px: 0.9,
                                py: 0.5,
                              }}
                            />
                          );
                        }

                        return (
                          <React.Fragment key={`line2-${header}-subheaders`}>
                            {!isLine2ActiveSerac && (
                            <TableCell
                              sx={{
                                bgcolor: theme.palette.mode === 'dark' ? '#111827' : '#f1f5f9',
                                color: theme.palette.mode === 'dark' ? '#cbd5e1' : '#475569',
                                position: 'sticky',
                                top: '32px',
                                zIndex: 3,
                                fontWeight: 600,
                                fontSize: '10px',
                                textAlign: 'center',
                                borderRight: '1px solid #475569',
                                borderBottom: '1px solid #475569',
                                minWidth: 56,
                                px: 0.9,
                                py: 0.5,
                              }}
                            >
                              Threshold
                            </TableCell>
                            )}
                            <TableCell
                              sx={{
                                bgcolor: theme.palette.mode === 'dark' ? '#111827' : '#f1f5f9',
                                color: theme.palette.mode === 'dark' ? '#cbd5e1' : '#475569',
                                position: 'sticky',
                                top: '32px',
                                zIndex: 3,
                                fontWeight: 600,
                                fontSize: '10px',
                                textAlign: 'center',
                                borderRight: headerIndex < line2TableHeaders.length - 1 ? '1px solid #475569' : 'none',
                                borderBottom: '1px solid #475569',
                                minWidth: 56,
                                px: 0.9,
                                py: 0.5,
                              }}
                            >
                              Ideal Value
                            </TableCell>
                          </React.Fragment>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {line2ActiveRows.map((row) => (
                      <TableRow key={`line2-${line2ActiveMachineOption?.key || line2ActiveTab}-${row.id}`}>
                        <TableCell
                          sx={{
                            bgcolor: theme.palette.mode === 'dark' ? '#111827' : '#ffffff',
                            color: theme.palette.mode === 'dark' ? '#e5e7eb' : '#111827',
                            fontSize: '12px',
                            fontWeight: 500,
                            borderRight: '1px solid #334155',
                            borderBottom: '1px solid #334155',
                            width: 150,
                            minWidth: 150,
                            px: 1,
                            py: 0.75,
                            whiteSpace: 'normal',
                            wordBreak: 'normal',
                            overflowWrap: 'normal',
                            lineHeight: 1.3,
                            verticalAlign: 'middle',
                          }}
                        >
                          {row.parameter}
                        </TableCell>
                        {line2DynamicSkuColumns.map((columnKey, columnIndex) => (
                          <React.Fragment key={`line2-${row.id}-${columnKey}`}>
                            {!isLine2ActiveSerac && (
                            <TableCell
                              sx={{
                                bgcolor: theme.palette.mode === 'dark' ? '#111827' : '#ffffff',
                                borderRight: '1px solid #334155',
                                borderBottom: '1px solid #334155',
                                minWidth: 56,
                                px: 0.7,
                                py: 0.55,
                              }}
                            >
                              {(() => {
                                const line2MachineKey = line2ActiveMachineOption?.key || '';
                                const canEditSku = line2MachineKey ? isSkuCellEditable(line2MachineKey, row, columnKey) : false;
                                return (
                              <Box
                                component="input"
                                value={parseMinMaxValue(row.values[columnKey] || '').min}
                                placeholder="Threshold"
                                disabled={!canEditSku}
                                title={canEditSku ? '' : 'No DB row for this parameter+SKU'}
                                onClick={(event: React.MouseEvent<HTMLInputElement>) => event.stopPropagation()}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                  handleSkuMinMaxChange(line2ActiveMachineOption?.key || '', row.id, columnKey, 'min', event.target.value)
                                }
                                inputMode="numeric"
                                autoComplete="off"
                                sx={{
                                  width: '100%',
                                  height: '26px',
                                  px: 0.6,
                                  border: theme.palette.mode === 'dark' ? '1px solid #475569' : '1px solid #cbd5e1',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  color: theme.palette.mode === 'dark' ? '#f8fafc' : '#111827',
                                  bgcolor: !canEditSku
                                    ? (theme.palette.mode === 'dark' ? '#1f2937' : '#f1f5f9')
                                    : (theme.palette.mode === 'dark' ? '#0b1220' : '#ffffff'),
                                  caretColor: theme.palette.mode === 'dark' ? '#f8fafc' : '#111827',
                                  outline: 'none',
                                  boxSizing: 'border-box',
                                  '&:focus': {
                                    borderColor: '#46A2FE',
                                    boxShadow: '0 0 0 1px #46A2FE',
                                  },
                                }}
                              />
                                );
                              })()}
                            </TableCell>
                            )}
                            <TableCell
                              sx={{
                                bgcolor: theme.palette.mode === 'dark' ? '#111827' : '#ffffff',
                                borderRight: columnIndex < line2DynamicSkuColumns.length - 1 ? '1px solid #334155' : 'none',
                                borderBottom: '1px solid #334155',
                                minWidth: 56,
                                px: 0.7,
                                py: 0.55,
                              }}
                            >
                              {(() => {
                                const line2MachineKey = line2ActiveMachineOption?.key || '';
                                const canEditSku = line2MachineKey ? isSkuCellEditable(line2MachineKey, row, columnKey) : false;
                                return (
                              <Box
                                component="input"
                                value={parseMinMaxValue(row.values[columnKey] || '').max}
                                placeholder="Ideal Value"
                                disabled={!canEditSku}
                                title={canEditSku ? '' : 'No DB row for this parameter+SKU'}
                                onClick={(event: React.MouseEvent<HTMLInputElement>) => event.stopPropagation()}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                  handleSkuMinMaxChange(line2ActiveMachineOption?.key || '', row.id, columnKey, 'max', event.target.value)
                                }
                                inputMode="numeric"
                                autoComplete="off"
                                sx={{
                                  width: '100%',
                                  height: '26px',
                                  px: 0.6,
                                  border: theme.palette.mode === 'dark' ? '1px solid #475569' : '1px solid #cbd5e1',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  color: theme.palette.mode === 'dark' ? '#f8fafc' : '#111827',
                                  bgcolor: !canEditSku
                                    ? (theme.palette.mode === 'dark' ? '#1f2937' : '#f1f5f9')
                                    : (theme.palette.mode === 'dark' ? '#0b1220' : '#ffffff'),
                                  caretColor: theme.palette.mode === 'dark' ? '#f8fafc' : '#111827',
                                  outline: 'none',
                                  boxSizing: 'border-box',
                                  '&:focus': {
                                    borderColor: '#46A2FE',
                                    boxShadow: '0 0 0 1px #46A2FE',
                                  },
                                }}
                              />
                                );
                              })()}
                            </TableCell>
                          </React.Fragment>
                        ))}
                      </TableRow>
                    ))}
                    {!isLine2ActiveTabLoading && line2ActiveRows.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={line2TotalColumns}
                          sx={{
                            bgcolor: theme.palette.mode === 'dark' ? '#111827' : '#ffffff',
                            color: theme.palette.mode === 'dark' ? '#e5e7eb' : '#111827',
                            textAlign: 'center',
                            py: 2,
                            fontSize: '12px',
                          }}
                        >
                          No records found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                {isLine2ActiveTabLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                      zIndex: 5,
                      bgcolor: 'rgba(15, 23, 42, 0.22)',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.25,
                        py: 0.75,
                        borderRadius: '6px',
                        bgcolor: 'rgba(15, 23, 42, 0.88)',
                        border: '1px solid #334155',
                      }}
                    >
                      <CircularProgress size={16} sx={{ color: '#46A2FE' }} />
                      <Typography component="span" sx={{ fontSize: '12px', color: '#e5e7eb' }}>
                        Loading configuration...
                      </Typography>
                    </Box>
                  </Box>
                )}
              </TableContainer>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1,
                  pt: 1,
                  px: 1,
                  pb: 0.5,
                  mt: 0.5,
                  borderTop: '1px solid #334155',
                  flexShrink: 0,
                  minHeight: '37px',
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => line2ActiveMachineOption && handleUpdate(line2ActiveMachineOption.key)}
                  disabled={
                    !line2ActiveMachineOption
                    || !loadedMachines[line2ActiveMachineOption.key]
                    || isLine2ActiveTabLoading
                    || (savingByMachine[line2ActiveMachineOption.key] || false)
                    || !hasLine2ActiveTabChanges
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: '#46A2FE',
                    '&:hover': {
                      bgcolor: '#2D8BEB',
                    },
                  }}
                >
                  {line2ActiveMachineOption && savingByMachine[line2ActiveMachineOption.key] ? (
                    <>
                      <CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} />
                      Saving...
                    </>
                  ) : (
                    'Update'
                  )}
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleNotificationClose} severity={notification.severity} variant="filled" sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AdminPanelDialog;
